import analyticsAdminService from '@/services/analyticsAdmin'

const normalizeDashboard = (dashboard) => ({
  id: dashboard.id,
  slug: dashboard.slug,
  name: dashboard.name,
  description: dashboard.description,
  workspaceId: dashboard.workspace_id,
  reportId: dashboard.report_id,
  datasetId: dashboard.dataset_id,
  rlsRole: dashboard.rls_role,
  authMode: dashboard.auth_mode,
  sortOrder: dashboard.sort_order,
  isGeneral: dashboard.is_general,
  isActive: dashboard.is_active,
  isComplete: dashboard.is_complete,
  missingFields: dashboard.missing_fields || [],
  organizationsCount: dashboard.organizations_count,
  usersCount: dashboard.users_count,
  createdAt: dashboard.created_at,
  updatedAt: dashboard.updated_at
})

const normalizeDetail = (dashboard) => ({
  ...normalizeDashboard(dashboard),
  organizations: (dashboard.organizations || []).map(organization => ({
    id: organization.id,
    name: organization.name,
    usersCount: organization.users_count,
    sortOrderOverride: organization.sort_order_override
  }))
})

// Un request nuevo de la misma lista aborta el anterior: su respuesta ya no se aplicaría (lo decide el request id),
// así que no hace falta que siga ocupando una conexión. El aborto rechaza la promesa y el `catch` lo ignora por esa misma guarda.
const controllers = {}

const startRequest = (key) => {
  controllers[key]?.abort()
  controllers[key] = new AbortController()

  return controllers[key].signal
}

// Un campo vacío viaja como null, igual que lo guarda el backend.
const blankToNull = (value) => {
  const trimmed = typeof value === 'string' ? value.trim() : value

  return trimmed === '' || trimmed === undefined ? null : trimmed
}

// El slug y el modo los fija el backend.
const toPayload = (form) => ({
  name: form.name,
  description: blankToNull(form.description),
  workspace_id: blankToNull(form.workspaceId),
  report_id: blankToNull(form.reportId),
  dataset_id: blankToNull(form.datasetId),
  rls_role: blankToNull(form.rlsRole),
  is_general: form.isGeneral,
  is_active: form.isActive
})

const normalizeOrganizationIdentity = (organization) => ({
  id: organization.id,
  name: organization.name,
  logo: organization.logo
})

const normalizeOrganization = (organization) => ({
  ...normalizeOrganizationIdentity(organization),
  rlsIdentity: organization.rls_identity,
  isActive: organization.is_active,
  needsAttention: organization.needs_attention,
  dashboardsCount: organization.dashboards_count,
  usersCount: organization.users_count
})

const normalizeOrganizationDetail = (organization) => ({
  ...normalizeOrganizationIdentity(organization),
  rlsIdentity: organization.rls_identity,
  isActive: organization.is_active,
  needsAttention: organization.needs_attention,
  usersCount: organization.users_count,
  organizationUsersCount: organization.organization_users_count,
  orderMode: organization.order_mode,
  dashboards: (organization.dashboards || []).map(dashboard => ({
    id: dashboard.id,
    name: dashboard.name,
    description: dashboard.description,
    isGeneral: dashboard.is_general,
    isActive: dashboard.is_active,
    isComplete: dashboard.is_complete,
    missingFields: dashboard.missing_fields || [],
    usersCount: dashboard.users_count,
    sortOrderOverride: dashboard.sort_order_override
  }))
})

const normalizeOrganizationUser = (user) => ({
  id: user.id,
  firstName: user.first_name,
  lastName: user.last_name,
  // Sin nombre se muestra el correo
  fullName: [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email,
  email: user.email,
  profileImage: user.profile_image,
  franchiseName: user.franchise_name,
  isSuspended: user.is_suspended,
  dashboardIds: user.dashboard_ids || []
})

const normalizeSummary = (summary) => ({
  organizationsCount: summary.organizations_count,
  activeOrganizationsCount: summary.active_organizations_count,
  usersWithAccessCount: summary.users_with_access_count,
  needsAttentionCount: summary.needs_attention_count
})

// El orden no viaja en `meta`: se reinyecta el que se pidió para que la tabla conserve sus flechas.
const toPagination = (meta, { sortBy, descending }) => ({
  page: meta.current_page,
  rowsPerPage: Number(meta.per_page),
  rowsNumber: meta.total,
  sortBy,
  descending
})

// El renglón que responden la pausa y la RLS actualiza su fila y, si está abierta, la ficha.
const applyOrganizationRow = (store, organization) => {
  const updated = normalizeOrganization(organization)

  store.organizations = store.organizations.map(current => (current.id === updated.id ? updated : current))

  if (store.currentOrganization?.id === updated.id) {
    store.currentOrganization = {
      ...store.currentOrganization,
      rlsIdentity: updated.rlsIdentity,
      isActive: updated.isActive,
      needsAttention: updated.needsAttention,
      usersCount: updated.usersCount
    }
  }

  return updated
}

export default {
  fetchSourceStatus () {
    return analyticsAdminService.fetchSourceStatus()
      .then(({ data }) => {
        this.sourceStatus = { driver: data.driver, isActive: data.is_active }
      })
      .catch(error => {
        // Solo alimenta un banner informativo: si falla no se afirma nada (`sourceStatus` sigue en null) y no se avisa.
        console.error(error)
      })
  },

  fetchDashboards () {
    this.dashboardsStatus = 'loading'

    return analyticsAdminService.fetchDashboards()
      .then(({ data }) => {
        this.dashboards = data.data.map(normalizeDashboard)
        this.dashboardsStatus = 'success'
      })
      .catch(error => {
        console.error(error)
        this.dashboardsStatus = 'error'
      })
  },

  // Al pasar de un tablero a otro, una ficha anterior que llega tarde no pisa a la vigente.
  fetchDashboard (dashboardId) {
    const requestId = ++this.currentDashboardRequestId
    const signal = startRequest('dashboard')
    this.currentDashboard = null
    this.currentDashboardStatus = 'loading'

    return analyticsAdminService.fetchDashboard(dashboardId, { signal })
      .then(({ data }) => {
        if (requestId !== this.currentDashboardRequestId) {
          return
        }

        this.currentDashboard = normalizeDetail(data.data)
        this.currentDashboardStatus = 'success'
      })
      .catch(error => {
        if (requestId !== this.currentDashboardRequestId) {
          return
        }

        console.error(error)
        this.currentDashboardStatus = 'error'
      })
  },

  // Alta y edición no tocan `dashboards`: la página recarga el catálogo al volver.
  // Los errores se propagan para que la página marque los campos del 422.
  async createDashboard (form) {
    const { data } = await analyticsAdminService.createDashboard(toPayload(form))

    return normalizeDashboard(data.data)
  },

  async updateDashboard (dashboardId, form) {
    const { data } = await analyticsAdminService.updateDashboard(dashboardId, toPayload(form))

    return normalizeDashboard(data.data)
  },

  async setDashboardStatus (dashboardId, isActive) {
    const { data } = await analyticsAdminService.setDashboardStatus(dashboardId, isActive)
    const updated = normalizeDashboard(data.data)

    this.dashboards = this.dashboards.map(dashboard => (dashboard.id === updated.id ? updated : dashboard))

    return updated
  },

  async reorderDashboards (orderedIds) {
    const { data } = await analyticsAdminService.reorderDashboards(orderedIds)

    this.dashboards = data.data.map(normalizeDashboard)
  },

  // Una búsqueda rápida lanza varios requests: solo se aplica la respuesta del último.
  fetchOrganizations ({ page = 1, rowsPerPage = 25, sortBy = 'name', descending = false, search = null, status = 'all' } = {}) {
    const requestId = ++this.organizationsRequestId
    const signal = startRequest('organizations')
    this.organizationsStatus = 'loading'

    return analyticsAdminService.fetchOrganizations({
      page,
      limit: rowsPerPage,
      sort_by: sortBy,
      sort_dir: descending ? 'desc' : 'asc',
      search: blankToNull(search),
      status
    }, { signal })
      .then(({ data }) => {
        if (requestId !== this.organizationsRequestId) {
          return
        }

        // La página dejó de existir (p. ej. se pausó la única fila de la última con un filtro): se pide la última.
        if (data.data.length === 0 && data.meta.current_page > data.meta.last_page) {
          return this.fetchOrganizations({ page: data.meta.last_page, rowsPerPage, sortBy, descending, search, status })
        }

        this.organizations = data.data.map(normalizeOrganization)
        this.organizationsPagination = toPagination(data.meta, { sortBy, descending })
        this.organizationsStatus = 'success'
      })
      .catch(error => {
        if (requestId !== this.organizationsRequestId) {
          return
        }

        console.error(error)
        this.organizationsStatus = 'error'
      })
  },

  fetchOrganizationsSummary () {
    this.organizationsSummaryStatus = 'loading'

    return analyticsAdminService.fetchOrganizationsSummary()
      .then(({ data }) => {
        this.organizationsSummary = normalizeSummary(data.data)
        this.organizationsSummaryStatus = 'success'
      })
      .catch(error => {
        console.error(error)
        this.organizationsSummaryStatus = 'error'
      })
  },

  // Opciones efímeras del diálogo de alta: no se guardan en el estado.
  async searchAvailableOrganizations (search) {
    const { data } = await analyticsAdminService.searchAvailableOrganizations(search)

    return data.data.map(normalizeOrganizationIdentity)
  },

  // Los errores (409 si ya tiene Analytics, 422 de la RLS o de los tableros) se propagan.
  async enableOrganization ({ organizationId, rlsIdentity, dashboardIds }) {
    const { data } = await analyticsAdminService.enableOrganization({
      organization_id: organizationId,
      rls_identity: blankToNull(rlsIdentity),
      dashboard_ids: dashboardIds
    })

    return normalizeOrganizationDetail(data.data)
  },

  // Al cambiar de organización, una respuesta anterior que llega tarde no pisa a la vigente.
  fetchOrganization (organizationId) {
    const requestId = ++this.currentOrganizationRequestId
    const signal = startRequest('organization')
    this.currentOrganization = null
    this.currentOrganizationStatus = 'loading'

    return analyticsAdminService.fetchOrganization(organizationId, { signal })
      .then(({ data }) => {
        if (requestId !== this.currentOrganizationRequestId) {
          return
        }

        this.currentOrganization = normalizeOrganizationDetail(data.data)
        this.currentOrganizationStatus = 'success'
      })
      .catch(error => {
        if (requestId !== this.currentOrganizationRequestId) {
          return
        }

        console.error(error)
        this.currentOrganizationStatus = 'error'
      })
  },

  async saveOrganizationDashboards (organizationId, { dashboardIds, orderMode }) {
    const { data } = await analyticsAdminService.saveOrganizationDashboards(organizationId, {
      dashboard_ids: dashboardIds,
      order_mode: orderMode
    })

    this.currentOrganization = normalizeOrganizationDetail(data.data)
  },

  async setOrganizationStatus (organizationId, isActive) {
    const { data } = await analyticsAdminService.setOrganizationStatus(organizationId, isActive)

    return applyOrganizationRow(this, data.data)
  },

  async updateOrganizationRlsIdentity (organizationId, rlsIdentity) {
    const { data } = await analyticsAdminService.updateOrganizationRlsIdentity(organizationId, rlsIdentity)

    return applyOrganizationRow(this, data.data)
  },

  // Igual que fetchOrganizations: solo se aplica la respuesta del último request.
  fetchOrganizationUsers (organizationId, {
    page = 1, rowsPerPage = 25, sortBy = 'name', descending = false, search = null, access = 'all', dashboardId = null,
    franchiseId = null
  } = {}) {
    const requestId = ++this.organizationUsersRequestId
    const signal = startRequest('organizationUsers')

    // De otra organización no se deja a la vista la lista anterior mientras carga.
    if (this.organizationUsersOrganizationId !== organizationId) {
      this.organizationUsers = []
      this.organizationUsersOrganizationId = organizationId
    }

    this.organizationUsersStatus = 'loading'

    return analyticsAdminService.fetchOrganizationUsers(organizationId, {
      page,
      limit: rowsPerPage,
      sort_by: sortBy,
      sort_dir: descending ? 'desc' : 'asc',
      search: blankToNull(search),
      access,
      dashboard_id: dashboardId,
      franchise_id: franchiseId
    }, { signal })
      .then(({ data }) => {
        if (requestId !== this.organizationUsersRequestId) {
          return
        }

        if (data.data.length === 0 && data.meta.current_page > data.meta.last_page) {
          return this.fetchOrganizationUsers(organizationId, {
            page: data.meta.last_page, rowsPerPage, sortBy, descending, search, access, dashboardId, franchiseId
          })
        }

        this.organizationUsers = data.data.map(normalizeOrganizationUser)
        this.organizationUsersPagination = toPagination(data.meta, { sortBy, descending })
        this.organizationUsersStatus = 'success'
      })
      .catch(error => {
        if (requestId !== this.organizationUsersRequestId) {
          return
        }

        console.error(error)
        // Sin lista: las filas anteriores eran de otro filtro u otra página y no deben seguir editables.
        this.organizationUsers = []
        this.organizationUsersPagination = { ...this.organizationUsersPagination, rowsNumber: 0 }
        this.organizationUsersStatus = 'error'
      })
  },

  // Opciones efímeras del filtro de unidad de negocio: no se guardan en el estado.
  async searchOrganizationFranchises (organizationId, search) {
    const { data } = await analyticsAdminService.fetchOrganizationFranchises(organizationId, blankToNull(search))

    return data.data.map(({ id, name }) => ({ id, name }))
  },

  // Da o quita acceso. El 409 (tablero ya no habilitado) y el 422 se propagan para que el panel los muestre.
  // Un guardado que falla no cuenta: la respuesta de uno anterior que llega después sí actualiza los conteos.
  async updateUserAccess (organizationId, { userIds, dashboardIds, hasAccess }) {
    const writeId = ++this.userAccessWriteId
    const { data } = await analyticsAdminService.updateUserAccess(organizationId, {
      user_ids: userIds,
      dashboard_ids: dashboardIds,
      has_access: hasAccess
    })
    const result = data.data
    const isLatestWrite = writeId > this.userAccessAppliedWriteId

    if (isLatestWrite) {
      this.userAccessAppliedWriteId = writeId
    }

    if (this.organizationUsersOrganizationId === organizationId) {
      const accessByUser = new Map(result.users.map(user => [user.id, user.dashboard_ids]))

      this.organizationUsers = this.organizationUsers.map(user => (
        accessByUser.has(user.id) ? { ...user, dashboardIds: accessByUser.get(user.id) } : user
      ))
    }

    // Solo cambian los conteos, en el mismo arreglo: quien observa los tableros no ve una ficha nueva.
    // Si ya se aplicó la respuesta de un guardado posterior, la de este puede traer conteos más viejos y no se aplican.
    if (this.currentOrganization?.id === organizationId && isLatestWrite) {
      const usersByDashboard = new Map(result.dashboards.map(dashboard => [dashboard.id, dashboard.users_count]))

      this.currentOrganization.usersCount = result.users_count
      this.currentOrganization.dashboards.forEach(dashboard => {
        if (usersByDashboard.has(dashboard.id)) {
          dashboard.usersCount = usersByDashboard.get(dashboard.id)
        }
      })
    }

    return { affectedUsersCount: result.affected_users_count }
  },

  // Como fetchOrganization, pero sin vaciar la ficha ni marcarla como cargando: la página no se tapa con el spinner.
  refreshOrganization (organizationId) {
    const requestId = ++this.currentOrganizationRequestId
    const signal = startRequest('organization')

    return analyticsAdminService.fetchOrganization(organizationId, { signal })
      .then(({ data }) => {
        if (requestId !== this.currentOrganizationRequestId) {
          return
        }

        this.currentOrganization = normalizeOrganizationDetail(data.data)
        this.currentOrganizationStatus = 'success'
      })
      .catch(error => {
        if (requestId !== this.currentOrganizationRequestId) {
          return
        }

        // Sin estado de error: la ficha vigente no se tapa. El panel ya avisó del 409 y su `loadUsers` avisa si el servidor falla.
        console.error(error)
      })
  }
}
